import React, { useEffect, useState, useContext } from 'react'
import {
  Container,
  Table,
  Button,
  Modal,
  ProgressBar,
  Spinner,
} from 'react-bootstrap'
import Papa from 'papaparse'
import axios from 'axios'
import Cookies from 'js-cookie'
import { AuthContext } from '../AuthContext'

const ContactSettings = () => {
  // 聯絡人資料狀態
  const [contacts, setContacts] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  // API 相關設定
  const { baseUrl, currentUser, extendCookie, setLoginShow } =
    useContext(AuthContext)
  const sessionFromCookie = Cookies.get('session')
  const { session = '' } = sessionFromCookie
    ? JSON.parse(sessionFromCookie)
    : {}

  const [currentPage, setCurrentPage] = useState(1)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [contactToDelete, setContactToDelete] = useState(null)
  const [itemsPerPage] = useState(10)

  // 聯絡人相關狀態
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingContact, setEditingContact] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [message, setMessage] = useState({ type: '', content: '' })
  const [newContact, setNewContact] = useState({
    name: '',
    tags: [],
    note: '',
    phones: '',
    inputMethod: 'manual', // 'manual' or 'csv'
  })
  const [tagInput, setTagInput] = useState('')
  const [csvFile, setCsvFile] = useState(null)
  const [csvPhones, setCsvPhones] = useState([])
  // 過往標籤歷史
  const [existingTags, setExistingTags] = useState([])
  const [isLoadingTags, setIsLoadingTags] = useState(false)
  const [filteredTags, setFilteredTags] = useState([])
  const [errors, setErrors] = useState({})

  // 取得聯絡人清單
  const fetchContactList = async () => {
    try {
      setIsLoading(true)
      setError(null)

      if (!session) {
        alert('登入已逾時，請重新登入')
        setLoginShow(true)
        throw new Error('Session not found')
      }

      const response = await axios.get(
        `${baseUrl}/api/backend/memberGroup/getList`,
        {
          headers: {
            'Content-Type': 'application/json',
            Accept: '*/*',
            session: currentUser?.session || session || '',
          },
        }
      )

      console.log('聯絡人清單 API 回應:', response.data)

      if (response.data && response.data.data) {
        // 將 API 資料轉換成前端需要的格式
        const transformedContacts = response.data.data.map((item) => ({
          id: item.member_group_id,
          name: item.name,
          count: item.count,
          tags: item.tags || [],
          note: item.description || '',
        }))

        setContacts(transformedContacts)
        extendCookie() // 延長 session
      } else if (response.data?.error?.code === '50004') {
        alert('登入已逾時，請重新登入')
        setLoginShow(true)
      } else {
        throw new Error('未收到預期的資料格式')
      }
    } catch (err) {
      console.error('取得聯絡人清單失敗:', err)

      setError('取得聯絡人清單時發生錯誤')
    } finally {
      setIsLoading(false)
    }
  }

  // 取得標籤清單
  const fetchTagsList = async () => {
    try {
      setIsLoadingTags(true)

      if (!session) {
        console.warn('Session not found for tags fetch')
        return
      }

      const response = await axios.get(`${baseUrl}/api/backend/getTags`, {
        headers: {
          'Content-Type': 'application/json',
          Accept: '*/*',
          session: currentUser?.session || session || '',
        },
      })

      console.log('標籤清單 API 回應:', response.data)

      if (response.data && response.data.data) {
        // 將 API 資料轉換成前端需要的格式
        const tags = response.data.data.map((item) => item.tag_name)
        setExistingTags(tags)
        extendCookie() // 延長 session
      } else if (response.data?.error?.code === '50004') {
        alert('登入已逾時，請重新登入')
        setLoginShow(true)
      } else {
        console.warn('未收到預期的標籤資料格式')
        // 如果 API 失敗，使用預設標籤
        setExistingTags(['企業', 'AI 健康套餐', '糖尿病風險'])
      }
    } catch (err) {
      console.error('取得標籤清單失敗:', err)
      alert('取得標籤清單失敗:', err)
    } finally {
      setIsLoadingTags(false)
    }
  }

  // 組件載入時取得資料
  useEffect(() => {
    if (session) {
      fetchContactList()
      fetchTagsList()
    } else {
      setLoginShow(true)
    }
  }, [session])

  // 分頁計算
  const totalPages = Math.ceil(contacts.length / itemsPerPage)
  const currentContacts = contacts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  )

  // 處理分頁變更
  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber)
    }
  }

  // 處理新增聯絡人
  const handleAddContact = () => {
    setNewContact({
      name: '',
      tags: [],
      note: '',
      phones: '',
      inputMethod: 'manual',
    })
    setTagInput('')
    setCsvFile(null)
    setCsvPhones([])
    setFilteredTags([])
    setErrors({})
    setShowAddModal(true)
  }

  // 處理新增聯絡人表單提交
  const handleSubmitNewContact = async () => {
    // 驗證表單
    const newErrors = {}

    if (!newContact.name.trim()) {
      newErrors.name = '請輸入聯絡人名稱'
    }

    if (!newContact.note.trim()) {
      newErrors.note = '請輸入備註'
    }

    if (newContact.inputMethod === 'manual') {
      if (!newContact.phones.trim()) {
        newErrors.phones = '請輸入手機號碼'
      }
    } else if (newContact.inputMethod === 'csv') {
      if (!csvFile || csvPhones.length === 0) {
        newErrors.csvFile = '請上傳包含有效電話號碼的 CSV 檔案'
      }
    }

    setErrors(newErrors)

    // 如果有錯誤，不提交
    if (Object.keys(newErrors).length > 0) {
      return
    }

    // 開始提交流程
    setIsSubmitting(true)
    setMessage({ type: '', content: '' })

    try {
      if (!session) {
        alert('登入已逾時，請重新登入')
        setLoginShow(true)
        throw new Error('Session not found')
      }

      // 準備手機號碼清單
      let memberList = []
      if (newContact.inputMethod === 'manual') {
        memberList = newContact.phones
          .split(',')
          .map((phone) => phone.trim())
          .filter((phone) => phone)
      } else if (newContact.inputMethod === 'csv') {
        memberList = csvPhones
      }

      // 準備 API 請求資料
      const requestData = {
        name: newContact.name.trim(),
        description: newContact.note.trim(),
        tags: newContact.tags,
        member_list: memberList,
      }

      console.log('新增聯絡人群組請求資料:', requestData)

      // 呼叫新增 API
      const response = await axios.post(
        `${baseUrl}/api/backend/memberGroup/add`,
        requestData,
        {
          headers: {
            'Content-Type': 'application/json',
            Accept: '*/*',
            session: currentUser?.session || session || '',
          },
        }
      )

      console.log('新增聯絡人群組 API 回應:', response.data)

      if (response.data && response.data.message === '新增成功') {
        // 延長 session
        extendCookie()

        // 新增成功後重新取得聯絡人清單
        await fetchContactList()

        // 關閉 Modal 並重置表單
        handleCancelAdd()

        // 顯示成功訊息
        alert('聯絡人群組新增成功！')
      } else if (response.data && response.data.error.code === '10016') {
        // 處理非會員手機號碼錯誤

        const errorMessage = response.data.error.message
        const phoneMatch = errorMessage.match(/\((\d+)\)/)
        const invalidPhone = phoneMatch ? phoneMatch[1] : ''
        console.warn('errorMessage:', errorMessage)
        console.warn('非會員手機號碼:', invalidPhone)

        if (newContact.inputMethod === 'manual') {
          setErrors((prev) => ({
            ...prev,
            phones: `手機號碼 ${invalidPhone} 不是會員，請檢查號碼或聯繫該用戶註冊。您可以移除此號碼後重新提交。`,
          }))

          // 高亮顯示問題號碼
          const currentPhones = newContact.phones
          const highlightedPhones = currentPhones.replace(
            new RegExp(invalidPhone, 'g'),
            `【${invalidPhone}】`
          )

          // 可選：自動將問題號碼標記出來
          setTimeout(() => {
            const phoneTextarea = document.querySelector(
              'textarea[placeholder*="手機號碼"]'
            )
            if (phoneTextarea) {
              phoneTextarea.focus()
              phoneTextarea.setSelectionRange(
                currentPhones.indexOf(invalidPhone),
                currentPhones.indexOf(invalidPhone) + invalidPhone.length
              )
            }
          }, 100)
        } else {
          setErrors((prev) => ({
            ...prev,
            csvFile: `CSV 中的手機號碼 ${invalidPhone} 不是會員，請移除此號碼或聯繫該用戶註冊後重新上傳檔案。`,
          }))

          // 從 CSV 數據中移除無效號碼
          // setCsvPhones((prev) => prev.filter((phone) => phone !== invalidPhone))
        }

        // 不關閉 Modal，讓用戶可以修正
      } else if (response.data?.error?.code === '50004') {
        alert('登入已逾時，請重新登入')
        setLoginShow(true)
      } else {
        throw new Error(response.data?.message || '新增失敗')
      }
    } catch (err) {
      console.error('新增聯絡人群組失敗:', err)

      alert('新增聯絡人群組時發生錯誤，請稍後再試')
    } finally {
      setIsSubmitting(false)
    }
  }

  // 處理取消新增
  const handleCancelAdd = () => {
    setShowAddModal(false)
    setNewContact({
      name: '',
      tags: [],
      note: '',
      phones: '',
      inputMethod: 'manual',
    })
    setTagInput('')
    setCsvFile(null)
    setCsvPhones([])
    setFilteredTags([])
    setErrors({})
  }

  // 處理輸入變更，同時清空錯誤
  const handleInputChange = (field, value) => {
    setNewContact({
      ...newContact,
      [field]: value,
    })

    // 清除相關錯誤
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }))
    }
  }

  // 處理標籤輸入
  const handleTagInputChange = (value) => {
    setTagInput(value)

    // 只要開始輸入標籤就清除標籤錯誤
    if (errors.tags && value.trim()) {
      setErrors((prev) => ({
        ...prev,
        tags: '',
      }))
    }

    if (value.trim()) {
      // 過濾出符合的標籤
      const filtered = existingTags.filter(
        (tag) =>
          tag.toLowerCase().includes(value.toLowerCase()) &&
          !newContact.tags.includes(tag)
      )
      setFilteredTags(filtered)
    } else {
      setFilteredTags([])
    }
  }

  // 添加標籤
  const addTag = (tag) => {
    if (!newContact.tags.includes(tag)) {
      setNewContact({
        ...newContact,
        tags: [...newContact.tags, tag],
      })
    }
    setTagInput('')
    setFilteredTags([])
  }

  // 移除標籤
  const removeTag = (tagToRemove) => {
    setNewContact({
      ...newContact,
      tags: newContact.tags.filter((tag) => tag !== tagToRemove),
    })
  }

  // 處理標籤輸入鍵盤事件
  const handleTagKeyPress = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      const trimmedTag = tagInput.trim()
      if (trimmedTag) {
        addTag(trimmedTag)
      }
    }
  }

  // CSV 解析函數
  const parseCsvToPhoneArray = (file) => {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: false, // 不使用表頭，直接讀取原始資料
        skipEmptyLines: true, // 跳過空行
        complete: (results) => {
          try {
            const phoneNumbers = []

            // 遍歷所有行和列，提取所有可能的電話號碼
            results.data.forEach((row) => {
              row.forEach((cell) => {
                if (cell && typeof cell === 'string') {
                  const cellStr = String(cell).trim()

                  // 處理可能的電話號碼格式
                  const processedPhone = processPhoneNumber(cellStr)

                  // 驗證處理後的電話號碼
                  if (isValidTaiwanPhone(processedPhone)) {
                    phoneNumbers.push(processedPhone)
                  }
                }
              })
            })

            // 去除重複的電話號碼
            const uniquePhones = [...new Set(phoneNumbers)]

            resolve(uniquePhones)
          } catch (error) {
            reject(new Error('CSV 解析失敗：' + error.message))
          }
        },
        error: (error) => {
          reject(new Error('CSV 讀取失敗：' + error.message))
        },
      })
    })
  }

  // 處理電話號碼格式
  const processPhoneNumber = (phone) => {
    if (!phone) return ''

    // 移除所有非數字字符（保留空格、破折號等分隔符）
    let cleaned = phone.replace(/[^\d]/g, '')

    // 處理常見的台灣手機號碼格式
    if (cleaned.length === 9 && cleaned.startsWith('9')) {
      // 如果是 9 開頭的 9 碼，補上前面的 0
      cleaned = '0' + cleaned
    } else if (cleaned.length === 10 && cleaned.startsWith('09')) {
      // 標準格式，直接使用
      return cleaned
    } else if (cleaned.length === 11 && cleaned.startsWith('886')) {
      // 國際格式 +886，轉換為本地格式
      cleaned = '0' + cleaned.substring(3)
    } else if (cleaned.length === 12 && cleaned.startsWith('8869')) {
      // 完整國際格式 +8869，轉換為本地格式
      cleaned = '09' + cleaned.substring(4)
    }
    // CSV 檔案可以是以下任一格式，都能正確處理：

    // 格式1 - 標準格式：
    // 0900000001,0900000002,0900000003

    // 格式2 - 被 Excel 處理過（缺少前導 0）：
    // 900000001,900000002,900000003
    // ↓ 自動補 0
    // 0900000001,0900000002,0900000003

    // 格式3 - 國際格式：
    // +886900000001,+886900000002
    // ↓ 轉換為本地格式
    // 0900000001,0900000002

    // 格式4 - 混合格式表格：
    // 姓名,電話,備註
    // 張三,900000001,VIP客戶
    // 李四,0900000002,一般客戶
    // 王五,+886900000003,重要客戶

    // 格式5 - 包含格式符號：
    // 09-0000-0001
    // 09 0000 0002
    // (09) 0000-0003

    return cleaned
  }

  // 驗證台灣手機號碼格式
  const isValidTaiwanPhone = (phone) => {
    const phoneRegex = /^09\d{8}$/
    return phoneRegex.test(phone)
  }

  // 處理 CSV 檔案上傳
  const handleCsvUpload = async (e) => {
    const file = e.target.files[0]
    setCsvFile(file)

    // 清除 CSV 相關錯誤
    if (errors.csvFile) {
      setErrors((prev) => ({
        ...prev,
        csvFile: '',
      }))
    }

    // 如果有選擇檔案，立即解析
    if (file) {
      try {
        const phoneArray = await parseCsvToPhoneArray(file)

        console.log('解析結果：', phoneArray)
        console.log('總計電話號碼數量：', phoneArray.length)

        // 可以將解析結果儲存到狀態中
        setCsvPhones(phoneArray) // 需要新增這個狀態
        console.log('csvPhones', csvPhones)

        // 或者直接更新 newContact
        setNewContact((prev) => ({
          ...prev,
          csvPhones: phoneArray,
        }))
      } catch (error) {
        console.error('CSV 解析錯誤：', error.message)
        setCsvPhones([])
        setErrors((prev) => ({
          ...prev,
          csvFile: error.message,
        }))
      }
    } else {
      // 如果沒有選擇檔案，清空結果
      setCsvPhones([])
    }
  }

  // useEffect(() => {
  //   console.log('csvphone', csvPhones)
  // }, [csvPhones])

  // 處理輸入方式變更
  const handleInputMethodChange = (method) => {
    setNewContact({
      ...newContact,
      inputMethod: method,
    })

    // 切換輸入方式時清除相關錯誤
    setErrors((prev) => ({
      ...prev,
      phones: '',
      csvFile: '',
    }))

    // 如果切換到手動輸入，清空 CSV 相關狀態
    if (method === 'manual') {
      setCsvFile(null)
      setCsvPhones([])
    }
  }

  // 取得特定群組資訊 (GetGroup)
  const fetchGroupDetails = async (memberGroupId) => {
    try {
      setIsLoading(true)

      if (!session) {
        alert('登入已逾時，請重新登入')
        setLoginShow(true)
        throw new Error('Session not found')
      }

      const response = await axios.post(
        `${baseUrl}/api/backend/memberGroup/get`,
        {
          member_group_id: memberGroupId.toString(),
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Accept: '*/*',
            session: currentUser?.session || session || '',
          },
        }
      )

      console.log('群組詳細資訊 API 回應:', response.data)

      if (response.data) {
        const groupData = response.data
        return {
          id: groupData.member_group_id,
          name: groupData.name,
          tags: groupData.tags || [],
          note: groupData.description || '',
          memberList: groupData.member_list || [],
          count: groupData.count,
        }
      } else if (response.data?.error?.code === '50004') {
        alert('登入已逾時，請重新登入')
        setLoginShow(true)
      } else {
        throw new Error('未收到預期的資料格式')
      }
    } catch (err) {
      console.error('取得群組詳細資訊失敗:', err)

      setError('取得群組資訊時發生錯誤')
      alert('取得群組資訊失敗')

      throw err
    } finally {
      setIsLoading(false)
    }
  }

  // 處理編輯聯絡人
  const handleEditContact = async (contactId) => {
    try {
      // 先取得完整的群組資訊
      const groupDetails = await fetchGroupDetails(contactId)

      setEditingContact(groupDetails)
      setNewContact({
        name: groupDetails.name,
        tags: [...groupDetails.tags],
        note: groupDetails.note,
        phones: groupDetails.memberList.join(', '), // 將成員列表轉換為逗號分隔的字串
        inputMethod: 'manual',
      })
      setCsvFile(null)
      setCsvPhones([])
      setTagInput('')
      setFilteredTags([])
      setErrors({})
      setShowEditModal(true)
    } catch (err) {
      console.error('處理編輯聯絡人失敗:', err)
    }
  }

  // 處理編輯聯絡人表單提交
  const handleSubmitEditContact = async () => {
    // 表單驗證
    const newErrors = {}

    if (!newContact.name.trim()) {
      newErrors.name = '請輸入聯絡人名稱'
    }

    if (!newContact.note.trim()) {
      newErrors.note = '請輸入備註'
    }

    if (newContact.inputMethod === 'manual') {
      if (!newContact.phones.trim()) {
        newErrors.phones = '請輸入手機號碼'
      }
    } else if (newContact.inputMethod === 'csv') {
      if (!csvFile || csvPhones.length === 0) {
        newErrors.csvFile = '請上傳包含有效電話號碼的 CSV 檔案'
      }
    }

    setErrors(newErrors)

    // 如果有錯誤，不提交
    if (Object.keys(newErrors).length > 0) {
      return
    }

    // 開始提交流程
    setIsSubmitting(true)
    setMessage({ type: '', content: '' })

    try {
      if (!session) {
        alert('登入已逾時，請重新登入')
        setLoginShow(true)
        throw new Error('Session not found')
      }

      // 準備手機號碼清單
      let memberList = []
      if (newContact.inputMethod === 'manual') {
        memberList = newContact.phones
          .split(',')
          .map((phone) => phone.trim())
          .filter((phone) => phone)
      } else if (newContact.inputMethod === 'csv') {
        memberList = csvPhones
      }

      // 準備 API 請求資料 (UpdateGroup)
      const requestData = {
        member_group_id: editingContact.id.toString(),
        name: newContact.name.trim(),
        description: newContact.note.trim(),
        tags: newContact.tags,
        member_list: memberList,
      }

      console.log('更新聯絡人群組請求資料:', requestData)

      // 呼叫更新 API
      const response = await axios.post(
        `${baseUrl}/api/backend/memberGroup/update`,
        requestData,
        {
          headers: {
            'Content-Type': 'application/json',
            Accept: '*/*',
            session: currentUser?.session || session || '',
          },
        }
      )

      console.log('更新聯絡人群組 API 回應:', response.data)

      if (
        response.data &&
        (response.data.message === '修改成功' || response.data.success)
      ) {
        // 延長 session
        extendCookie()

        // 更新成功後重新取得聯絡人清單
        await fetchContactList()

        // 關閉 Modal 並重置表單
        handleCancelEdit()

        // 顯示成功訊息
        alert('聯絡人群組更新成功！')
      } else if (response.data && response.data.error.code === '10016') {
        // 處理非會員手機號碼錯誤

        const errorMessage = response.data.error.message
        const phoneMatch = errorMessage.match(/\((\d+)\)/)
        const invalidPhone = phoneMatch ? phoneMatch[1] : ''
        console.warn('errorMessage:', errorMessage)
        console.warn('非會員手機號碼:', invalidPhone)

        if (newContact.inputMethod === 'manual') {
          setErrors((prev) => ({
            ...prev,
            phones: `手機號碼 ${invalidPhone} 不是會員，請檢查號碼或聯繫該用戶註冊。您可以移除此號碼後重新提交。`,
          }))

          // 高亮顯示問題號碼
          const currentPhones = newContact.phones
          const highlightedPhones = currentPhones.replace(
            new RegExp(invalidPhone, 'g'),
            `【${invalidPhone}】`
          )

          // 可選：自動將問題號碼標記出來
          setTimeout(() => {
            const phoneTextarea = document.querySelector(
              'textarea[placeholder*="手機號碼"]'
            )
            if (phoneTextarea) {
              phoneTextarea.focus()
              phoneTextarea.setSelectionRange(
                currentPhones.indexOf(invalidPhone),
                currentPhones.indexOf(invalidPhone) + invalidPhone.length
              )
            }
          }, 100)
        } else {
          setErrors((prev) => ({
            ...prev,
            csvFile: `CSV 中的手機號碼 ${invalidPhone} 不是會員，請移除此號碼或聯繫該用戶註冊後重新上傳檔案。`,
          }))

          // 從 CSV 數據中移除無效號碼
          setCsvPhones((prev) => prev.filter((phone) => phone !== invalidPhone))
        }

        // 不關閉 Modal，讓用戶可以修正
      } else if (response.data?.error?.code === '50004') {
        alert('登入已逾時，請重新登入')
        setLoginShow(true)
      } else {
        throw new Error(response.data?.error?.message || '更新失敗')
      }
    } catch (err) {
      console.error('更新聯絡人群組失敗:', err)

      alert('更新聯絡人群組失敗，更新時發生未知錯誤')
    } finally {
      setIsSubmitting(false)
    }
  }

  // 處理取消編輯
  const handleCancelEdit = () => {
    setShowEditModal(false)
    setEditingContact(null)
    setNewContact({
      name: '',
      tags: [],
      note: '',
      phones: '',
      inputMethod: 'manual',
    })
    setTagInput('')
    setCsvFile(null)
    setCsvPhones([])
    setFilteredTags([])
    setErrors({})
    setMessage({ type: '', content: '' })
  }

  // 處理刪除聯絡人
  const handleDeleteContact = (contact) => {
    setContactToDelete(contact)
    setShowDeleteModal(true)
  }

  // 確認刪除
  const confirmDelete = async () => {
    if (!contactToDelete) return

    try {
      setIsSubmitting(true)

      if (!session) {
        alert('登入已逾時，請重新登入')
        setLoginShow(true)
        throw new Error('Session not found')
      }

      console.log('準備刪除群組:', contactToDelete)

      // 呼叫刪除 API
      const response = await axios.post(
        `${baseUrl}/api/backend/memberGroup/delete`,
        {
          member_group_id: contactToDelete.id.toString(),
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Accept: '*/*',
            session: currentUser?.session || session || '',
          },
        }
      )

      console.log('刪除群組 API 回應:', response.data)

      if (response.data && response.data.message === '删除成功') {
        // 延長 session
        extendCookie()
        // 從本地狀態中移除已刪除的聯絡人群組
        setContacts((prevContacts) =>
          prevContacts.filter((contact) => contact.id !== contactToDelete.id)
        )

        // 調整分頁 - 如果當前頁面沒有項目了，回到上一頁
        const remainingCount = contacts.length - 1
        const maxPage = Math.ceil(remainingCount / itemsPerPage)
        if (currentPage > maxPage && maxPage > 0) {
          setCurrentPage(maxPage)
        }

        // 關閉 Modal 並重置狀態
        setShowDeleteModal(false)
        setContactToDelete(null)

        // 顯示成功訊息
        alert(`群組「${contactToDelete.name}」已成功刪除！`)
      } else if (response.data?.error?.code === '50004') {
        alert('登入已逾時，請重新登入')
        setLoginShow(true)
      } else {
        throw new Error(response.data?.error?.message || '刪除失敗')
      }
    } catch (err) {
      console.error('刪除聯絡人群組失敗:', err)

      alert('刪除聯絡人群組失敗，刪除時發生未知錯誤')
    } finally {
      setIsSubmitting(false)
    }
  }

  // 取消刪除
  const cancelDelete = () => {
    if (isSubmitting) return

    setShowDeleteModal(false)
    setContactToDelete(null)
  }

  // 標籤組件
  const TagPill = ({ tag }) => {
    return (
      <span
        style={{
          padding: '4px 12px',
          borderRadius: '20px',
          backgroundColor: '#212529',
          color: 'white',
          display: 'inline-block',
          fontSize: '12px',
          marginRight: '4px',
          marginBottom: '2px',
          position: 'relative',
        }}
      >
        {tag}
      </span>
    )
  }

  // 重新整理資料
  const handleRefresh = () => {
    fetchContactList()
  }

  // 載入狀態
  if (isLoading) {
    return (
      <Container fluid className="py-4" style={{ maxWidth: '1400px' }}>
        <div className="m-5">
          <ProgressBar className="m-4" animated now={100} />
          <h5 className="text-secondary text-center">資料等待中...</h5>
        </div>
      </Container>
    )
  }

  // 聯絡人資料錯誤狀態
  if (error) {
    return (
      <Container fluid className="py-4" style={{ maxWidth: '1400px' }}>
        <div className="text-center py-5">
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
          <Button variant="primary" onClick={handleRefresh}>
            重新載入
          </Button>
        </div>
      </Container>
    )
  }

  return (
    <Container fluid className="py-4" style={{ maxWidth: '1400px' }}>
      {/* 標題與新增按鈕 */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">聯絡人設定</h2>
        <Button
          variant="primary"
          onClick={handleAddContact}
          style={{
            backgroundColor: '##67c7aa',
            borderColor: '##67c7aa',
            borderRadius: '8px',
            padding: '8px 16px',
          }}
        >
          <i className="fa-solid fa-plus me-2"></i>
          新增
        </Button>
      </div>

      {/* 聯絡人表格 */}
      <div className="bg-white rounded-3">
        <Table hover responsive className="mb-0">
          <thead className="table-light">
            <tr>
              <th style={{ width: '200px' }}>名稱</th>
              <th style={{ width: '100px' }}>人數</th>
              <th style={{ width: '300px' }}>標籤</th>
              <th style={{ width: '200px' }}>備註</th>
              <th style={{ width: '120px' }}>進階操作</th>
            </tr>
          </thead>
          <tbody>
            {currentContacts.length > 0 ? (
              currentContacts.map((contact) => (
                <tr key={contact.id}>
                  <td>{contact.name}</td>
                  <td>{contact.count}</td>
                  <td>
                    <div className="d-flex flex-wrap gap-1">
                      {contact.tags.map((tag, index) => (
                        <TagPill key={index} tag={tag} />
                      ))}
                    </div>
                  </td>
                  <td>{contact.note}</td>
                  <td>
                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleEditContact(contact.id)}
                        title="編輯"
                      >
                        <i className="fa-solid fa-edit"></i>
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDeleteContact(contact)}
                        title="刪除"
                      >
                        <i className="fa-solid fa-trash"></i>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="text-center py-4 text-muted">
                  <i className="fa-solid fa-inbox fa-2x mb-3"></i>
                  <div>目前沒有聯絡人資料</div>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* 分頁控制 */}
      {totalPages > 1 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: '40px',
            gap: '8px',
          }}
        >
          <div
            onClick={() => handlePageChange(currentPage - 1)}
            style={{
              padding: '4px 8px',
              borderRadius: '4px',
              color: currentPage <= 1 ? '#ccc' : '#666',
              cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
            }}
          >
            <i className="fa-solid fa-angle-left" />
          </div>

          {/* Page numbers with ellipsis */}
          {Array.from({ length: totalPages }).map((_, index) => {
            // Show first page, last page, current page, and pages around current
            const shouldShow =
              index === 0 || // First page
              index === totalPages - 1 || // Last page
              Math.abs(index - (currentPage - 1)) <= 2 // Pages around current

            // Show ellipsis instead of page numbers
            if (!shouldShow && (index === 1 || index === totalPages - 2)) {
              return (
                <div key={index} style={{ color: '#666' }}>
                  <i className="fa-solid fa-ellipsis" />
                </div>
              )
            }

            // Hide other page numbers
            if (!shouldShow) {
              return null
            }

            return (
              <div
                key={index}
                onClick={() => handlePageChange(index + 1)}
                style={{
                  padding: '4px 8px',
                  borderRadius: '4px',
                  backgroundColor:
                    currentPage === index + 1 ? '#67c7aa' : 'white',
                  color: currentPage === index + 1 ? 'white' : 'black',
                  cursor: 'pointer',
                }}
              >
                {index + 1}
              </div>
            )
          })}

          <div
            onClick={() => handlePageChange(currentPage + 1)}
            style={{
              padding: '4px 8px',
              borderRadius: '4px',
              color: currentPage >= totalPages ? '#ccc' : '#666',
              cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
            }}
          >
            <i className="fa-solid fa-angle-right" />
          </div>
        </div>
      )}

      {/* 編輯聯絡人 Modal */}
      <Modal show={showEditModal} onHide={handleCancelEdit} centered size="lg">
        <Modal.Header
          style={{
            backgroundColor: 'white',
          }}
        >
          <Modal.Title
            style={{
              width: '100%',
              textAlign: 'center',
              margin: 0,
              color: '#2c3e50',
            }}
          >
            編輯聯絡人
          </Modal.Title>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={handleCancelEdit}
            style={{ position: 'absolute', right: '1rem' }}
          ></button>
        </Modal.Header>
        <Modal.Body className="p-4">
          {/* 聯絡人名稱 */}
          <div className="mb-4">
            <label className="form-label mb-2">
              聯絡人名稱 <span style={{ color: '#dc3545' }}>*</span>
            </label>
            <input
              type="text"
              className="form-control"
              placeholder="請輸入聯絡人名稱"
              value={newContact.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              style={{
                border: errors.name ? '2px solid #dc3545' : '1px solid #dee2e6',
                borderRadius: '6px',
              }}
            />
            {errors.name && (
              <div
                style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}
              >
                {errors.name}
              </div>
            )}
          </div>

          {/* 標籤 */}
          <div className="mb-4">
            <label className="form-label mb-2">標籤</label>
            <div className="mb-2">
              {newContact.tags.map((tag, index) => (
                <span
                  key={index}
                  className="badge me-1 mb-1"
                  style={{
                    backgroundColor: '#212529',
                    color: 'white',
                    fontSize: '12px',
                    padding: '6px 12px',
                    borderRadius: '20px',
                    cursor: 'pointer',
                  }}
                  onClick={() => removeTag(tag)}
                >
                  {tag} ×
                </span>
              ))}
            </div>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                className="form-control"
                placeholder="請輸入標籤"
                value={tagInput}
                onChange={(e) => handleTagInputChange(e.target.value)}
                onKeyPress={handleTagKeyPress}
                style={{
                  border: errors.tags
                    ? '2px solid #dc3545'
                    : '1px solid #dee2e6',
                  borderRadius: '6px',
                }}
              />
              {errors.tags && (
                <div
                  style={{
                    color: '#dc3545',
                    fontSize: '12px',
                    marginTop: '4px',
                  }}
                >
                  {errors.tags}
                </div>
              )}
              {filteredTags.length > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: '1px solid #dee2e6',
                    borderTop: 'none',
                    borderRadius: '0 0 4px 4px',
                    maxHeight: '150px',
                    overflowY: 'auto',
                    zIndex: 1000,
                  }}
                >
                  {filteredTags.map((tag, index) => (
                    <div
                      key={index}
                      className="px-3 py-2"
                      style={{
                        cursor: 'pointer',
                        borderBottom:
                          index < filteredTags.length - 1
                            ? '1px solid #f8f9fa'
                            : 'none',
                      }}
                      onMouseEnter={(e) =>
                        (e.target.style.backgroundColor = '#f8f9fa')
                      }
                      onMouseLeave={(e) =>
                        (e.target.style.backgroundColor = 'white')
                      }
                      onClick={() => addTag(tag)}
                    >
                      {tag}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* 備註 */}
          <div className="mb-4">
            <label className="form-label mb-2">
              備註 <span style={{ color: '#dc3545' }}>*</span>
            </label>
            <input
              type="text"
              className="form-control"
              placeholder="請輸入備註"
              value={newContact.note}
              onChange={(e) => handleInputChange('note', e.target.value)}
              style={{
                border: errors.note ? '2px solid #dc3545' : '1px solid #dee2e6',
                borderRadius: '6px',
              }}
            />
            {errors.note && (
              <div
                style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}
              >
                {errors.note}
              </div>
            )}
          </div>

          {/* 帳號輸入方式選擇 */}
          <div className="mb-3">
            <label className="form-label mb-2">
              帳號 <span style={{ color: '#dc3545' }}>*</span>
            </label>
            <div className="d-flex gap-3 mb-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="editInputMethod"
                  id="editManual"
                  checked={newContact.inputMethod === 'manual'}
                  onChange={() => handleInputMethodChange('manual')}
                />
                <label className="form-check-label" htmlFor="editManual">
                  手機號碼
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="editInputMethod"
                  id="editCsv"
                  checked={newContact.inputMethod === 'csv'}
                  onChange={() => handleInputMethodChange('csv')}
                />
                <label className="form-check-label" htmlFor="editCsv">
                  匯入 csv 檔案
                </label>
              </div>
            </div>

            {/* 手機號碼輸入 */}
            {newContact.inputMethod === 'manual' && (
              <div>
                <textarea
                  className="form-control"
                  rows="4"
                  placeholder="請輸入使用者的手機號碼（例：0900000000）&#10;若要輸入多組手機號碼，請以逗號（,）做為區隔"
                  value={newContact.phones}
                  onChange={(e) => handleInputChange('phones', e.target.value)}
                  style={{
                    border: errors.phones
                      ? '2px solid #dc3545'
                      : '1px solid #dee2e6',
                    borderRadius: '6px',
                  }}
                />
                {errors.phones && (
                  <div className="invalid-feedback d-block">
                    <i className="fa-solid fa-exclamation-triangle me-1"></i>
                    {errors.phones}
                    {errors.phones.includes('不是會員') && (
                      <div className="mt-2">
                        <button
                          type="button"
                          className="btn btn-outline-warning btn-sm"
                          onClick={() => {
                            // 提取錯誤訊息中的手機號碼
                            const phoneMatch = errors.phones.match(/(\d{10})/)
                            if (phoneMatch) {
                              const invalidPhone = phoneMatch[1]
                              const updatedPhones = newContact.phones
                                .split(',')
                                .map((p) => p.trim())
                                .filter((p) => p !== invalidPhone)
                                .join(', ')

                              handleInputChange('phones', updatedPhones)
                              setErrors((prev) => ({ ...prev, phones: '' }))
                            }
                          }}
                        >
                          <i className="fa-solid fa-trash me-1"></i>
                          移除無效號碼
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* CSV 檔案上傳 */}
            {newContact.inputMethod === 'csv' && (
              <div>
                <input
                  type="file"
                  className="form-control"
                  accept=".csv"
                  onChange={handleCsvUpload}
                  style={{
                    border: errors.csvFile
                      ? '2px solid #dc3545'
                      : '1px solid #dee2e6',
                    borderRadius: '6px',
                  }}
                />
                {errors.csvFile && (
                  <div className="invalid-feedback d-block">
                    <i className="fa-solid fa-exclamation-triangle me-1"></i>
                    {errors.csvFile}
                    {errors.csvFile.includes('不是會員') && (
                      <div className="mt-2">
                        <small className="text-muted d-block mb-2">
                          無效的號碼已自動從清單中移除，您可以直接重新提交
                        </small>
                      </div>
                    )}
                  </div>
                )}
                {csvFile && !errors.csvFile && (
                  <div className="mt-2 text-success">
                    <i className="fa-solid fa-check me-1"></i>
                    已選擇檔案：{csvFile.name}
                  </div>
                )}

                {/* 顯示 CSV 解析結果 */}
                {csvPhones.length > 0 && (
                  <div className="mt-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      總計：{csvPhones.length} 筆手機號碼
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer
          style={{ backgroundColor: '#f8f9fa', borderTop: '1px solid #dee2e6' }}
        >
          <Button variant="secondary" onClick={handleCancelEdit}>
            取消
          </Button>
          <Button variant="primary" onClick={handleSubmitEditContact}>
            儲存
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 新增聯絡人 Modal */}
      <Modal show={showAddModal} onHide={handleCancelAdd} centered size="lg">
        <Modal.Header
          style={{
            backgroundColor: 'white',
          }}
        >
          <Modal.Title
            style={{
              width: '100%',
              textAlign: 'center',
              margin: 0,
              color: '#2c3e50',
            }}
          >
            新增聯絡人
          </Modal.Title>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={handleCancelAdd}
            style={{ position: 'absolute', right: '1rem' }}
          ></button>
        </Modal.Header>
        <Modal.Body className="p-4">
          {/* 聯絡人名稱 */}
          <div className="mb-4">
            <label className="form-label mb-2">
              聯絡人名稱<span style={{ color: '#dc3545' }}>*</span>
            </label>
            <input
              type="text"
              className="form-control"
              placeholder="請輸入聯絡人名稱"
              value={newContact.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              style={{
                border: errors.name ? '2px solid #dc3545' : '1px solid #dee2e6',
                borderRadius: '6px',
              }}
            />
            {errors.name && (
              <div
                style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}
              >
                {errors.name}
              </div>
            )}
          </div>

          {/* 標籤 */}
          <div className="mb-4">
            <label className="form-label mb-2">標籤</label>
            <div className="mb-2">
              {newContact.tags.map((tag, index) => (
                <span
                  key={index}
                  className="badge me-1 mb-1"
                  style={{
                    backgroundColor: '#212529',
                    color: 'white',
                    fontSize: '12px',
                    padding: '6px 12px',
                    borderRadius: '20px',
                    cursor: 'pointer',
                  }}
                  onClick={() => removeTag(tag)}
                >
                  {tag} ×
                </span>
              ))}
            </div>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                className="form-control"
                placeholder="請輸入標籤"
                value={tagInput}
                onChange={(e) => handleTagInputChange(e.target.value)}
                onKeyPress={handleTagKeyPress}
                style={{
                  border: errors.tags
                    ? '2px solid #dc3545'
                    : '1px solid #dee2e6',
                  borderRadius: '6px',
                }}
              />
              {errors.tags && (
                <div
                  style={{
                    color: '#dc3545',
                    fontSize: '12px',
                    marginTop: '4px',
                  }}
                >
                  {errors.tags}
                </div>
              )}
              {filteredTags.length > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: '1px solid #dee2e6',
                    borderTop: 'none',
                    borderRadius: '0 0 4px 4px',
                    maxHeight: '150px',
                    overflowY: 'auto',
                    zIndex: 1000,
                  }}
                >
                  {filteredTags.map((tag, index) => (
                    <div
                      key={index}
                      className="px-3 py-2"
                      style={{
                        cursor: 'pointer',
                        borderBottom:
                          index < filteredTags.length - 1
                            ? '1px solid #f8f9fa'
                            : 'none',
                      }}
                      onMouseEnter={(e) =>
                        (e.target.style.backgroundColor = '#f8f9fa')
                      }
                      onMouseLeave={(e) =>
                        (e.target.style.backgroundColor = 'white')
                      }
                      onClick={() => addTag(tag)}
                    >
                      {tag}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* 備註 */}
          <div className="mb-4">
            <label className="form-label mb-2">
              備註<span style={{ color: '#dc3545' }}>*</span>
            </label>
            <input
              type="text"
              className="form-control"
              placeholder="請輸入備註"
              value={newContact.note}
              onChange={(e) => handleInputChange('note', e.target.value)}
              style={{
                border: errors.note ? '2px solid #dc3545' : '1px solid #dee2e6',
                borderRadius: '6px',
              }}
            />
            {errors.note && (
              <div
                style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}
              >
                {errors.note}
              </div>
            )}
          </div>

          {/* 帳號輸入方式選擇 */}
          <div className="mb-3">
            <label className="form-label mb-2">
              帳號<span style={{ color: '#dc3545' }}>*</span>
            </label>
            <div className="d-flex gap-3 mb-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="inputMethod"
                  id="manual"
                  checked={newContact.inputMethod === 'manual'}
                  onChange={() => handleInputMethodChange('manual')}
                />
                <label className="form-check-label" htmlFor="manual">
                  手機號碼
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="inputMethod"
                  id="csv"
                  checked={newContact.inputMethod === 'csv'}
                  onChange={() => handleInputMethodChange('csv')}
                />
                <label className="form-check-label" htmlFor="csv">
                  匯入 csv 檔案
                </label>
              </div>
            </div>

            {/* 手機號碼輸入 */}
            {newContact.inputMethod === 'manual' && (
              <div>
                <textarea
                  className="form-control"
                  rows="4"
                  placeholder="請輸入使用者的手機號碼（例：0900000000）&#10;若要輸入多組手機號碼，請以逗號（,）做為區隔"
                  value={newContact.phones}
                  onChange={(e) => handleInputChange('phones', e.target.value)}
                  style={{
                    border: errors.phones
                      ? '2px solid #dc3545'
                      : '1px solid #dee2e6',
                    borderRadius: '6px',
                  }}
                />
                {errors.phones && (
                  <div className="invalid-feedback d-block">
                    <i className="fa-solid fa-exclamation-triangle me-1"></i>
                    {errors.phones}
                    {errors.phones.includes('不是會員') && (
                      <div className="mt-2">
                        <button
                          type="button"
                          className="btn btn-outline-warning btn-sm"
                          onClick={() => {
                            // 提取錯誤訊息中的手機號碼
                            const phoneMatch = errors.phones.match(/(\d{10})/)
                            if (phoneMatch) {
                              const invalidPhone = phoneMatch[1]
                              const updatedPhones = newContact.phones
                                .split(',')
                                .map((p) => p.trim())
                                .filter((p) => p !== invalidPhone)
                                .join(', ')

                              handleInputChange('phones', updatedPhones)
                              setErrors((prev) => ({ ...prev, phones: '' }))
                            }
                          }}
                        >
                          <i className="fa-solid fa-trash me-1"></i>
                          移除無效號碼
                        </button>
                      </div>
                    )}
                  </div>
                )}
                <small className="form-text text-muted">
                  多個手機號碼請用逗號分隔，所有號碼必須是已註冊會員
                </small>
              </div>
            )}

            {/* CSV 檔案上傳 */}
            {newContact.inputMethod === 'csv' && (
              <div>
                <input
                  type="file"
                  className="form-control"
                  accept=".csv"
                  onChange={handleCsvUpload}
                  style={{
                    border: errors.csvFile
                      ? '2px solid #dc3545'
                      : '1px solid #dee2e6',
                    borderRadius: '6px',
                  }}
                />
                {errors.csvFile && (
                  <div className="invalid-feedback d-block">
                    <i className="fa-solid fa-exclamation-triangle me-1"></i>
                    {errors.csvFile}
                    {errors.csvFile.includes('不是會員') && (
                      <div className="mt-2">
                        <small className="text-muted d-block mb-2">
                          無效的號碼已自動從清單中移除，您可以直接重新提交
                        </small>
                      </div>
                    )}
                  </div>
                )}
                {csvFile && !errors.csvFile && (
                  <div className="mt-2 text-success">
                    <i className="fa-solid fa-check me-1"></i>
                    已選擇檔案：{csvFile.name}
                  </div>
                )}

                {csvPhones.length > 0 && (
                  <div className="mt-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      總計：{csvPhones.length} 筆手機號碼
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer
          style={{ backgroundColor: '#f8f9fa', borderTop: '1px solid #dee2e6' }}
        >
          <Button variant="secondary" onClick={handleCancelAdd}>
            取消
          </Button>
          <Button variant="primary" onClick={handleSubmitNewContact}>
            儲存
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 刪除確認 Modal */}
      <Modal show={showDeleteModal} onHide={cancelDelete} centered size="lg">
        <Modal.Header
          closeButton
          style={{
            backgroundColor: 'white',
          }}
        >
          <Modal.Title
            style={{
              width: '100%',
              textAlign: 'center',
              margin: 0,
              color: '#2c3e50',
            }}
          >
            刪除聯絡人
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-5">
          {contactToDelete && (
            <div>
              <p className="mb-0" style={{ fontSize: '16px' }}>
                請確認是否刪除聯絡人「{contactToDelete.name}」？
              </p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={cancelDelete}>
            取消
          </Button>
          <Button
            variant="danger"
            onClick={confirmDelete}
            disabled={isSubmitting}
            className="px-4"
          >
            {isSubmitting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  className="me-2"
                />
                刪除中...
              </>
            ) : (
              <>確認刪除</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}

export default ContactSettings
